import { useEffect, useRef, useState } from 'react';

function getTargetBlock(clientId) {
  // 1. Try frontend or normal DOM
  let target = document.querySelector(`[data-block="${clientId}"]`);
  
  if (target) return target;

  // 2. Try inside iframe (Site Editor case)
  const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe'); // fallback
  if (iframe && iframe.contentDocument) {
    target = iframe.contentDocument.querySelector(`[data-block="${clientId}"]`);
    if (target) return target;
  }

  // 3. Not found
  return null;
}

export const MouseFollowerType1 = ({ clientId, style }) => {
	const canvasRef = useRef(null);
	const [visible, setVisible] = useState(false);
	const pos = useRef({ x: 0, y: 0 });
	const mouse = useRef({ x: 0, y: 0 });

	const dotColor = style?.background || 'rgba(0, 255, 255, 0.5)';
	const dotSize = style?.size || 10;
	const smoothness = style?.decay || 0.1;

	useEffect(() => {
		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');

		const resize = () => {
			canvas.width = window.innerWidth;
			canvas.height = window.innerHeight;
		};
		resize();
		window.addEventListener('resize', resize);

		const render = () => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			pos.current.x += (mouse.current.x - pos.current.x) * smoothness;
			pos.current.y += (mouse.current.y - pos.current.y) * smoothness;

			ctx.beginPath();
			ctx.arc(pos.current.x, pos.current.y, dotSize, 0, Math.PI * 2);
			ctx.fillStyle = dotColor;
			ctx.shadowBlur = 20;
			ctx.shadowColor = dotColor;
			ctx.fill();

			requestAnimationFrame(render);
		};
		render();

        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const handleMove = (e) => {
            if (!visible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top;
            }

            mouse.current = { x, y };
        };

		const target = getTargetBlock(clientId);
		if (!target) return;

		const show = () => setVisible(true);
		const hide = () => setVisible(false);

		const listenerTarget = iframe?.contentWindow || window;

		listenerTarget.addEventListener('mousemove', handleMove);
		target.addEventListener('mouseenter', show);
		target.addEventListener('mouseleave', hide);

		return () => {
			listenerTarget.removeEventListener('mousemove', handleMove);
			window.removeEventListener('resize', resize);
			target.removeEventListener('mouseenter', show);
			target.removeEventListener('mouseleave', hide);
		};
	}, [clientId, visible, dotColor, dotSize, smoothness]);

	return (
		<canvas
			ref={canvasRef}
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				width: '100vw',
				height: '100vh',
				pointerEvents: 'none',
				zIndex: 99999999,
				display: visible ? 'block' : 'none',
			}}
		/>
	);
};

export const MouseFollowerType2 = ({ clientId, style }) => {
    const canvasRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const positions = useRef([]);

    const dotColor = style?.background || 'rgba(0, 123, 255, 1)';
    const dotSize = style?.size || 6;

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        let animationFrameId;

        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < positions.current.length; i++) {
                const { x, y, alpha } = positions.current[i];
                ctx.beginPath();
                ctx.arc(x, y, dotSize, 0, Math.PI * 2);
                ctx.fillStyle = applyAlphaToColor(dotColor, alpha);
                ctx.fill();
            }

            positions.current = positions.current
                .map(p => ({ ...p, alpha: p.alpha - 0.02 }))
                .filter(p => p.alpha > 0);

            animationFrameId = requestAnimationFrame(draw);
        };

        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const handleMouseMove = (e) => {
            if (!isVisible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top - 50;
            }

             positions.current.push({ x: x, y: y, alpha: 1 });
        };

        const targetElement = getTargetBlock(clientId);;
        if (!targetElement) return;

        const handleEnter = () => setIsVisible(true);
        const handleLeave = () => setIsVisible(false);

        document.addEventListener('mousemove', handleMouseMove);
        targetElement.addEventListener('mouseenter', handleEnter);
        targetElement.addEventListener('mouseleave', handleLeave);

        draw();

        return () => {
            cancelAnimationFrame(animationFrameId);
            document.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('resize', resizeCanvas);
            if (targetElement) {
                targetElement.removeEventListener('mouseenter', handleEnter);
                targetElement.removeEventListener('mouseleave', handleLeave);
            }
        };
    }, [clientId, isVisible, dotColor, dotSize]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 9999,
                display: isVisible ? 'block' : 'none',
            }}
        />
    );
};

function applyAlphaToColor(color, alpha) {
    if (color.startsWith('rgba')) return color; // already rgba
    if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color; // fallback
}

export const MouseFollowerType3 = ({ clientId, style }) => {
    const canvasRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const particles = useRef([]);

    const getRGBA = (color, alpha) => {
        if (!color) return `rgba(173,216,230,${alpha})`; // default lightblue
        if (color.startsWith('rgba')) {
            return color.replace(/[\d.]+\)$/g, `${alpha})`);
        }
        if (color.startsWith('rgb')) {
            return color.replace('rgb', 'rgba').replace(/\)$/, `,${alpha})`);
        }
        const hex = color.replace('#', '');
        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r},${g},${b},${alpha})`;
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', resize);

        const spawn = (x, y) => {
            for (let i = 0; i < 3; i++) {
                particles.current.push({
                    x,
                    y,
                    dx: (Math.random() - 0.5) * 2,
                    dy: (Math.random() - 0.5) * 2,
                    alpha: 1,
                    size: Math.random() * 4 + 2,
                });
            }
        };

        let lastTime = 0;
        const fpsInterval = 1000 / 20; // 30fps for slower animation, increase to slow down more

        const draw = (time = 0) => {
            if (time - lastTime > fpsInterval) {
                lastTime = time;

                ctx.clearRect(0, 0, canvas.width, canvas.height);
                particles.current.forEach(p => {
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                    ctx.fillStyle = getRGBA(style?.background, p.alpha);
                    ctx.fill();
                });

                particles.current = particles.current
                    .map(p => ({
                        ...p,
                        x: p.x + p.dx,
                        y: p.y + p.dy,
                        alpha: p.alpha - 0.02,
                    }))
                    .filter(p => p.alpha > 0);
            }
            requestAnimationFrame(draw);
        };
        draw();


        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const handleMove = (e) => {
            if (!visible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top - 50;
            }

             spawn( x, y);
        };

        const target = getTargetBlock(clientId);
        if (!target) return;

        const show = () => setVisible(true);
        const hide = () => setVisible(false);

        document.addEventListener('mousemove', handleMove);
        target.addEventListener('mouseenter', show);
        target.addEventListener('mouseleave', hide);

        return () => {
            document.removeEventListener('mousemove', handleMove);
            window.removeEventListener('resize', resize);
            target.removeEventListener('mouseenter', show);
            target.removeEventListener('mouseleave', hide);
        };
    }, [clientId, visible, style]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 9999,
                display: visible ? 'block' : 'none',
            }}
        />
    );
};

export const MouseFollowerType4 = ({ clientId, style }) => {
    const canvasRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const points = useRef([]);

    const strokeColor = style?.background || 'rgba(255, 105, 180, 1)'; // default pink

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', resize);

        const render = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            ctx.beginPath();
            for (let i = 0; i < points.current.length - 1; i++) {
                const p1 = points.current[i];
                const p2 = points.current[i + 1];

                // If color has alpha (rgba), replace alpha dynamically
                const colorWithAlpha = strokeColor.includes('rgba')
                    ? strokeColor.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, `rgba($1, $2, $3, ${p1.alpha})`)
                    : strokeColor;

                ctx.strokeStyle = colorWithAlpha;
                ctx.lineWidth = 2;
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
            }

            points.current = points.current
                .map(p => ({ ...p, alpha: p.alpha - 0.02 }))
                .filter(p => p.alpha > 0);

            requestAnimationFrame(render);
        };
        render();

        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const handleMove = (e) => {
            if (!visible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top - 50;
            }

            points.current.push({ x: x, y: y, alpha: 1 });
        };

        const target = getTargetBlock(clientId);
        if (!target) return;

        const show = () => setVisible(true);
        const hide = () => setVisible(false);

        document.addEventListener('mousemove', handleMove);
        target.addEventListener('mouseenter', show);
        target.addEventListener('mouseleave', hide);

        return () => {
            document.removeEventListener('mousemove', handleMove);
            window.removeEventListener('resize', resize);
            target.removeEventListener('mouseenter', show);
            target.removeEventListener('mouseleave', hide);
        };
    }, [clientId, visible, strokeColor]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 9999,
                display: visible ? 'block' : 'none',
            }}
        />
    );
};

export const MouseFollowerType5 = ({ clientId, style }) => {
    const canvasRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const pos = useRef({ x: 0, y: 0 });
    const mouse = useRef({ x: 0, y: 0 });

    const getRGBA = (color, alpha) => {
        if (!color) return `rgba(0, 200, 255, ${alpha})`; // default to your original blue-ish
        if (color.startsWith('rgba')) {
            return color.replace(/[\d.]+\)$/g, `${alpha})`);
        }
        if (color.startsWith('rgb')) {
            return color.replace('rgb', 'rgba').replace(/\)$/, `,${alpha})`);
        }
        const hex = color.replace('#', '');
        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r},${g},${b},${alpha})`;
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', resize);

        const render = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            pos.current.x += (mouse.current.x - pos.current.x) * 0.12;
            pos.current.y += (mouse.current.y - pos.current.y) * 0.12;

            const pulse = 18 + Math.sin(Date.now() / 200) * 2;

            // Outer Glow Ring
            ctx.beginPath();
            ctx.arc(pos.current.x, pos.current.y, pulse, 0, Math.PI * 2);
            ctx.strokeStyle = getRGBA(style?.background, 0.25);
            ctx.lineWidth = 2.5;
            ctx.shadowBlur = 25;
            ctx.shadowColor = getRGBA(style?.background, 0.6);
            ctx.stroke();

            // Glowing inner dot
            ctx.beginPath();
            ctx.arc(pos.current.x, pos.current.y, 5, 0, Math.PI * 2);
            ctx.fillStyle = getRGBA('#fff', 0.9); // white inner dot, keep as is
            ctx.shadowBlur = 12;
            ctx.shadowColor = getRGBA(style?.background, 1);
            ctx.fill();

            requestAnimationFrame(render);
        };
        render();

        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const onMove = (e) => {
            if (!visible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top - 50;
            }

            mouse.current = { x: x, y: y};
        };

        const target = getTargetBlock(clientId);
        if (!target) return;

        const show = () => setVisible(true);
        const hide = () => setVisible(false);

        document.addEventListener('mousemove', onMove);
        target.addEventListener('mouseenter', show);
        target.addEventListener('mouseleave', hide);

        return () => {
            document.removeEventListener('mousemove', onMove);
            window.removeEventListener('resize', resize);
            target.removeEventListener('mouseenter', show);
            target.removeEventListener('mouseleave', hide);
        };
    }, [clientId, visible, style]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 9999,
                display: visible ? 'block' : 'none',
                mixBlendMode: 'screen',
            }}
        />
    );
};

export const MouseFollowerType6 = ({ clientId}) => {
    const canvasRef = useRef(null);
    const requestId = useRef(null);
    const mousePos = useRef({ x: 0, y: 0 });
    const trail = useRef([]);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Set canvas size
        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        // Create a flower petal shape path
        const drawFlowerPetal = (x, y, radius, color, rotation) => {
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(rotation);
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
                ctx.lineTo(
                    Math.cos((i * Math.PI) / 3) * radius,
                    Math.sin((i * Math.PI) / 3) * radius
                );
            }
            ctx.closePath();
            ctx.fillStyle = color;
            ctx.shadowColor = color;
            ctx.shadowBlur = radius * 1.5;
            ctx.fill();
            ctx.restore();
        };

        const maxTrail = 25; // how many trailing shadows

        // Animation loop
        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Add current mouse pos to trail
            trail.current.push({
                x: mousePos.current.x,
                y: mousePos.current.y,
                alpha: 1,
                radius: 30 + Math.sin(Date.now() / 400) * 6,
                rotation: (Date.now() / 1000) % (Math.PI * 2),
                color: `hsl(${(Date.now() / 10) % 360}, 80%, 60%)`,
            });

            // Keep trail length in limit
            if (trail.current.length > maxTrail) {
                trail.current.shift();
            }

            // Draw the trail with decreasing alpha and radius
            for (let i = 0; i < trail.current.length; i++) {
                const t = trail.current[i];
                ctx.globalAlpha = t.alpha * (1 - i / maxTrail);
                drawFlowerPetal(t.x, t.y, t.radius * (1 - i / maxTrail), t.color, t.rotation);

                // Fade alpha for next frame
                t.alpha -= 0.03;
                if (t.alpha < 0) t.alpha = 0;
            }
            ctx.globalAlpha = 1;

            // Draw main glowing circle on cursor
            ctx.beginPath();
            const mainRadius = 12 + Math.sin(Date.now() / 200) * 3;
            const mainColor = `hsl(${(Date.now() / 10) % 360}, 90%, 70%)`;
            ctx.arc(mousePos.current.x, mousePos.current.y, mainRadius, 0, Math.PI * 2);
            ctx.fillStyle = mainColor;
            ctx.shadowColor = mainColor;
            ctx.shadowBlur = 20;
            ctx.fill();

            requestId.current = requestAnimationFrame(animate);
        };

        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const onMove = (e) => {
            if (!visible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top - 50;
            }

            mousePos.current.x = x;
            mousePos.current.y = y;
        };

        // Target element to watch
        const target = getTargetBlock(clientId);
        if (!target) return;

        const show = () => {
            setVisible(true);
            // reset trail on show to avoid artifacts
            trail.current = [];
        };
        const hide = () => {
            setVisible(false);
            trail.current = [];
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        };

        document.addEventListener('mousemove', onMove);
        target.addEventListener('mouseenter', show);
        target.addEventListener('mouseleave', hide);

        if (visible) {
            animate();
        }

        return () => {
            document.removeEventListener('mousemove', onMove);
            window.removeEventListener('resize', resize);
            target.removeEventListener('mouseenter', show);
            target.removeEventListener('mouseleave', hide);
            cancelAnimationFrame(requestId.current);
        };
    }, [clientId, visible]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 9999,
                display: visible ? 'block' : 'none',
                mixBlendMode: 'screen',
                userSelect: 'none',
            }}
        />
    );
};

export const MouseFollowerType7 = ({ clientId}) => {
    const canvasRef = useRef(null);
    const requestId = useRef(null);
    const mousePos = useRef({ x: 0, y: 0 });
    const particles = useRef([]);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        class Particle {
            constructor(x, y) {
                this.x = x;
                this.y = y;
                this.size = Math.random() * 2 + 1; // Small random size
                this.life = Math.random() * 60 + 30; // Life in frames
                this.alpha = 1;
                this.velocity = {
                    x: (Math.random() - 0.5) * 2, // Random horizontal velocity
                    y: (Math.random() - 0.5) * 2  // Random vertical velocity
                };
                this.color = `hsl(${Math.random() * 360}, 80%, 70%)`;
            }

            update() {
                this.x += this.velocity.x;
                this.y += this.velocity.y;
                this.life--;
                this.alpha = Math.max(0, this.life / 60); // Fade out
                this.size *= 0.98; // Shrink
            }

            draw() {
                ctx.save();
                ctx.globalAlpha = this.alpha;
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fillStyle = this.color;
                ctx.shadowColor = this.color;
                ctx.shadowBlur = this.size * 5; // Glowing effect
                ctx.fill();
                ctx.restore();
            }
        }

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (visible) {
                // Add new particles at mouse position
                for (let i = 0; i < 3; i++) { // Add 3 particles per frame
                    particles.current.push(new Particle(mousePos.current.x, mousePos.current.y));
                }
            }

            // Update and draw particles
            for (let i = particles.current.length - 1; i >= 0; i--) {
                const p = particles.current[i];
                p.update();
                if (p.life <= 0 || p.size < 0.5) {
                    particles.current.splice(i, 1); // Remove dead particles
                } else {
                    p.draw();
                }
            }

            requestId.current = requestAnimationFrame(animate);
        };

        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const onMove = (e) => {
            if (!visible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top - 50;
            }

            mousePos.current.x = x;
            mousePos.current.y = y;
        };

        const target = getTargetBlock(clientId);
        if (!target) return;

        const show = () => {
            setVisible(true);
            particles.current = []; // Clear particles on show
        };
        const hide = () => {
            setVisible(false);
            // Don't clear particles immediately, let them fade out
        };

        document.addEventListener('mousemove', onMove);
        target.addEventListener('mouseenter', show);
        target.addEventListener('mouseleave', hide);

        animate(); // Start animation loop regardless of visibility to allow fade out

        return () => {
            document.removeEventListener('mousemove', onMove);
            window.removeEventListener('resize', resize);
            target.removeEventListener('mouseenter', show);
            target.removeEventListener('mouseleave', hide);
            cancelAnimationFrame(requestId.current);
        };
    }, [clientId, visible]); // Include visible in dependencies to react to state changes

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 9999,
                display: visible || particles.current.length > 0 ? 'block' : 'none', // Keep canvas visible as long as particles exist
                mixBlendMode: 'screen', // Good for glowing effects
                userSelect: 'none',
            }}
        />
    );
};

export const MouseFollowerType8 = ({ clientId}) => {
    const canvasRef = useRef(null);
    const requestId = useRef(null);
    const mousePos = useRef({ x: 0, y: 0 });
    const glitchRects = useRef([]);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const createGlitchRect = (x, y) => {
            return {
                x: x + (Math.random() - 0.5) * 50, // Offset from mouse
                y: y + (Math.random() - 0.5) * 50,
                width: Math.random() * 30 + 10,
                height: Math.random() * 30 + 10,
                life: 30 + Math.random() * 30, // Life in frames
                alpha: 1,
                color: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 1)`,
            };
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (visible) {
                // Add new glitch rectangles
                if (Math.random() < 0.6) { // Probability of adding a new glitch
                    glitchRects.current.push(createGlitchRect(mousePos.current.x, mousePos.current.y));
                }
            }

            // Update and draw glitch rectangles
            for (let i = glitchRects.current.length - 1; i >= 0; i--) {
                const rect = glitchRects.current[i];
                rect.life--;
                rect.alpha = Math.max(0, rect.life / 60); // Fade out

                ctx.globalAlpha = rect.alpha;
                ctx.fillStyle = rect.color;
                ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

                if (rect.life <= 0) {
                    glitchRects.current.splice(i, 1); // Remove dead rectangles
                }
            }
            ctx.globalAlpha = 1; // Reset global alpha

            // Draw a subtle primary indicator at the cursor
            if (visible) {
                const mainSize = 8 + Math.sin(Date.now() / 150) * 2;
                const mainColor = `hsl(${(Date.now() / 10) % 360}, 90%, 70%)`;
                ctx.beginPath();
                ctx.arc(mousePos.current.x, mousePos.current.y, mainSize, 0, Math.PI * 2);
                ctx.fillStyle = mainColor;
                ctx.fill();
            }

            requestId.current = requestAnimationFrame(animate);
        };

        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const onMove = (e) => {
            if (!visible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top - 50;
            }

            mousePos.current.x = x;
            mousePos.current.y = y;
        };


        const target = getTargetBlock(clientId);
        if (!target) return;

        const show = () => {
            setVisible(true);
            glitchRects.current = []; // Clear existing glitches
        };
        const hide = () => {
            setVisible(false);
            // Allow glitches to fade out naturally
        };

        document.addEventListener('mousemove', onMove);
        target.addEventListener('mouseenter', show);
        target.addEventListener('mouseleave', hide);

        animate();

        return () => {
            document.removeEventListener('mousemove', onMove);
            window.removeEventListener('resize', resize);
            target.removeEventListener('mouseenter', show);
            target.removeEventListener('mouseleave', hide);
            cancelAnimationFrame(requestId.current);
        };
    }, [clientId, visible]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 9999,
                display: visible || glitchRects.current.length > 0 ? 'block' : 'none', // Keep canvas visible if glitches are still fading
                mixBlendMode: 'difference', // Creates an interesting color inversion effect
                userSelect: 'none',
            }}
        />
    );
};

export const MouseFollowerType9 = ({ clientId}) => {
    const canvasRef = useRef(null);
    const requestId = useRef(null);
    const mousePos = useRef({ x: 0, y: 0 });
    const orbPos = useRef({ x: 0, y: 0 }); // Orb's actual position, smoothly interpolated
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (visible) {
                // Smoothly interpolate orb position towards mouse position
                orbPos.current.x += (mousePos.current.x - orbPos.current.x) * 0.15;
                orbPos.current.y += (mousePos.current.y - orbPos.current.y) * 0.15;

                // Draw the main orb
                const mainRadius = 25 + Math.sin(Date.now() / 300) * 5; // Pulsing effect
                const mainColor = `hsl(${(Date.now() / 20) % 360}, 70%, 50%)`;

                ctx.beginPath();
                ctx.arc(orbPos.current.x, orbPos.current.y, mainRadius, 0, Math.PI * 2);
                ctx.fillStyle = mainColor;
                ctx.shadowColor = mainColor;
                ctx.shadowBlur = mainRadius * 1.5; // Soft glow
                ctx.fill();

                // Draw subtle distortions/secondary shapes
                ctx.globalAlpha = 0.6;
                ctx.beginPath();
                ctx.arc(orbPos.current.x + Math.sin(Date.now() / 150) * 10,
                        orbPos.current.y + Math.cos(Date.now() / 200) * 8,
                        mainRadius * 0.7, 0, Math.PI * 2);
                ctx.fillStyle = `hsl(${(Date.now() / 20 + 30) % 360}, 60%, 60%)`; // Slightly different hue
                ctx.fill();
                ctx.globalAlpha = 1;
            }

            requestId.current = requestAnimationFrame(animate);
        };

        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const onMove = (e) => {
            if (!visible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top - 50;
            }

            mousePos.current.x = x;
            mousePos.current.y = y;
        };

        const target = getTargetBlock(clientId);
        if (!target) return;

        const show = () => {
            setVisible(true);
            // Reset orb position on show to prevent jumping from previous state
            orbPos.current.x = mousePos.current.x;
            orbPos.current.y = mousePos.current.y;
        };
        const hide = () => {
            setVisible(false);
            // Allow orb to fade out or quickly move off-screen if desired
            // For this example, it just stops updating and becomes display: none
        };

        document.addEventListener('mousemove', onMove);
        target.addEventListener('mouseenter', show);
        target.addEventListener('mouseleave', hide);

        animate();

        return () => {
            document.removeEventListener('mousemove', onMove);
            window.removeEventListener('resize', resize);
            target.removeEventListener('mouseenter', show);
            target.removeEventListener('mouseleave', hide);
            cancelAnimationFrame(requestId.current);
        };
    }, [clientId, visible]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 9999,
                display: visible ? 'block' : 'none',
                mixBlendMode: 'screen',
                userSelect: 'none',
            }}
        />
    );
};

export const MouseFollowerType10 = ({ clientId}) => {
    const canvasRef = useRef(null);
    const requestId = useRef(null);
    const mousePos = useRef({ x: 0, y: 0 });
    const blobPos = useRef({ x: 0, y: 0 }); // Smoothly interpolated blob position
    const ripples = useRef([]);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (visible) {
                // Smoothly interpolate blob position
                blobPos.current.x += (mousePos.current.x - blobPos.current.x) * 0.1;
                blobPos.current.y += (mousePos.current.y - blobPos.current.y) * 0.1;

                // Add new ripple
                if (Math.random() < 0.2) { // Less frequent but larger ripples
                    ripples.current.push({
                        x: blobPos.current.x,
                        y: blobPos.current.y,
                        radius: 0,
                        alpha: 0.8,
                        color: `hsl(${(Date.now() / 30) % 360}, 70%, 70%)`
                    });
                }
            }

            // Update and draw ripples
            for (let i = ripples.current.length - 1; i >= 0; i--) {
                const r = ripples.current[i];
                r.radius += 1.5; // Expand
                r.alpha -= 0.015; // Fade out

                ctx.globalAlpha = Math.max(0, r.alpha);
                ctx.beginPath();
                ctx.arc(r.x, r.y, r.radius, 0, Math.PI * 2);
                ctx.strokeStyle = r.color;
                ctx.lineWidth = 2;
                ctx.shadowColor = r.color;
                ctx.shadowBlur = r.radius / 2;
                ctx.stroke();

                if (r.alpha <= 0) {
                    ripples.current.splice(i, 1);
                }
            }
            ctx.globalAlpha = 1; // Reset alpha

            // Draw the main blob
            if (visible) {
                const mainRadius = 18 + Math.sin(Date.now() / 250) * 4; // Gentle pulsing
                const mainColor = `hsl(${(Date.now() / 20) % 360}, 80%, 65%)`;

                ctx.beginPath();
                ctx.arc(blobPos.current.x, blobPos.current.y, mainRadius, 0, Math.PI * 2);
                ctx.fillStyle = mainColor;
                ctx.shadowColor = mainColor;
                ctx.shadowBlur = mainRadius * 2;
                ctx.fill();
            }

            requestId.current = requestAnimationFrame(animate);
        };

        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const onMove = (e) => {
            if (!visible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top - 50;
            }

            mousePos.current.x = x;
            mousePos.current.y = y;
        };


        const target = getTargetBlock(clientId);
        if (!target) return;

        const show = () => {
            setVisible(true);
            blobPos.current.x = mousePos.current.x; // Set initial blob position
            blobPos.current.y = mousePos.current.y;
            ripples.current = []; // Clear ripples on show
        };
        const hide = () => {
            setVisible(false);
            // Ripples can fade out naturally
        };

        document.addEventListener('mousemove', onMove);
        target.addEventListener('mouseenter', show);
        target.addEventListener('mouseleave', hide);

        animate();

        return () => {
            document.removeEventListener('mousemove', onMove);
            window.removeEventListener('resize', resize);
            target.removeEventListener('mouseenter', show);
            target.removeEventListener('mouseleave', hide);
            cancelAnimationFrame(requestId.current);
        };
    }, [clientId, visible]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 9999,
                display: visible || ripples.current.length > 0 ? 'block' : 'none',
                mixBlendMode: 'screen', // Creates beautiful glowing effects
                userSelect: 'none',
            }}
        />
    );
};

export const MouseFollowerType11 = ({ clientId}) => {
    const canvasRef = useRef(null);
    const requestId = useRef(null);
    const mousePos = useRef({ x: 0, y: 0 });
    const particles = useRef([]);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            particles.current = []; // Clear particles on resize
        };
        resize();
        window.addEventListener('resize', resize);

        class Particle {
            constructor(x, y) {
                this.x = x;
                this.y = y;
                this.vx = (Math.random() - 0.5) * 0.5; // Very subtle movement
                this.vy = (Math.random() - 0.5) * 0.5;
                this.life = 120 + Math.random() * 60; // Longer lifespan
                this.alpha = 0; // Starts transparent
                this.size = 2; // Small size
                this.maxDist = 100; // Max distance for lines
            }

            update() {
                this.x += this.vx;
                this.y += this.vy;
                this.life--;
                this.alpha = Math.min(1, Math.max(0, (180 - this.life) / 60)); // Fade in, then fade out
                if (this.life < 60) this.alpha = Math.max(0, this.life / 60);
            }

            draw() {
                ctx.globalAlpha = this.alpha;
                ctx.fillStyle = `hsl(${(Date.now() / 50) % 360}, 80%, 70%)`;
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (visible) {
                // Add new particles near mouse
                if (Math.random() < 0.8) {
                    particles.current.push(new Particle(mousePos.current.x + (Math.random() - 0.5) * 20, mousePos.current.y + (Math.random() - 0.5) * 20));
                }
            }

            // Update and draw particles
            for (let i = particles.current.length - 1; i >= 0; i--) {
                const p = particles.current[i];
                p.update();
                if (p.life <= 0) {
                    particles.current.splice(i, 1);
                } else {
                    p.draw();

                    // Draw lines between nearby particles
                    for (let j = i + 1; j < particles.current.length; j++) {
                        const p2 = particles.current[j];
                        const dist = Math.sqrt(Math.pow(p.x - p2.x, 2) + Math.pow(p.y - p2.y, 2));

                        if (dist < p.maxDist) {
                            ctx.globalAlpha = p.alpha * p2.alpha * (1 - dist / p.maxDist) * 0.5; // Lines fade with particles and distance
                            ctx.strokeStyle = `hsl(${(Date.now() / 50) % 360}, 80%, 70%)`;
                            ctx.lineWidth = 1;
                            ctx.beginPath();
                            ctx.moveTo(p.x, p.y);
                            ctx.lineTo(p2.x, p2.y);
                            ctx.stroke();
                        }
                    }
                }
            }
            ctx.globalAlpha = 1;

            requestId.current = requestAnimationFrame(animate);
        };

        const iframe = document.querySelector('iframe[name="editor-canvas"]') || document.querySelector('iframe');
        
        const onMove = (e) => {
            if (!visible) return;

            let x = e.clientX;
            let y = e.clientY;

            // Check if inside iframe
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();
                x += iframeRect.left + 100;
                y += iframeRect.top - 50;
            }

            mousePos.current.x = x;
            mousePos.current.y = y;
        };


        const target = getTargetBlock(clientId);
        if (!target) return;

        const show = () => {
            setVisible(true);
            particles.current = []; // Clear particles on show to start fresh
        };
        const hide = () => {
            setVisible(false);
            // Particles will naturally fade out due to life cycle
        };

        document.addEventListener('mousemove', onMove);
        target.addEventListener('mouseenter', show);
        target.addEventListener('mouseleave', hide);

        animate();

        return () => {
            document.removeEventListener('mousemove', onMove);
            window.removeEventListener('resize', resize);
            target.removeEventListener('mouseenter', show);
            target.removeEventListener('mouseleave', hide);
            cancelAnimationFrame(requestId.current);
        };
    }, [clientId, visible]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 9999,
                display: visible || particles.current.length > 0 ? 'block' : 'none',
                mixBlendMode: 'screen', // Good for techy glow
                userSelect: 'none',
            }}
        />
    );
};

// export const MouseFollowerType12 = ({ clientId, style}) => {
//   const [isVisible, setIsVisible] = useState(false);
//   const [trail, setTrail] = useState([]);

//   useEffect(() => {
//     const targetElement = getTargetBlock(clientId);
//     if (!targetElement) return;

//     const handleMouseMove = (e) => {
//       if (!isVisible) return;

//       const newDot = {
//         id: Date.now() + Math.random(),
//         x: e.clientX,
//         y: e.clientY,
//         content: (
//           <div
//             style={{
//               background: '#ff69b4',
//               color: '#fff',
//               padding: '4px 8px',
//               borderRadius: '999px',
//               fontSize: '14px',
//               fontWeight: 'bold',
//               boxShadow: '0 0 6px rgba(0,0,0,0.2)',
//             }}
//           >
//             🌸 Flower
//           </div>
//         ),
//       };

//       setTrail((prev) => [...prev, newDot]);

//       // remove after 600ms
//       setTimeout(() => {
//         setTrail((prev) => prev.filter((dot) => dot.id !== newDot.id));
//       }, 600);
//     };

//     const handleEnter = () => setIsVisible(true);
//     const handleLeave = () => setIsVisible(false);

//     document.addEventListener('mousemove', handleMouseMove);
//     targetElement.addEventListener('mouseenter', handleEnter);
//     targetElement.addEventListener('mouseleave', handleLeave);

//     return () => {
//       document.removeEventListener('mousemove', handleMouseMove);
//       targetElement.removeEventListener('mouseenter', handleEnter);
//       targetElement.removeEventListener('mouseleave', handleLeave);
//     };
//   }, [clientId, isVisible]);

//   return (
//     <div
//       style={{
//         position: 'fixed',
//         top: 0,
//         left: 0,
//         width: '100vw',
//         height: '100vh',
//         pointerEvents: 'none',
//         zIndex: 9999,
//       }}
//     >
//       {trail.map((dot) => (
//         <div
//           key={dot.id}
//           style={{
//             position: 'absolute',
//             top: dot.y,
//             left: dot.x,
//             transform: 'translate(-50%, -50%)',
//             transition: 'opacity 0.3s ease',
//           }}
//         >
//           {dot.content}
//         </div>
//       ))}
//     </div>
//   );
// };

// export const MouseFollowerType13 = ({ clientId, style}) => {
//   const followerRef = useRef(null);
//   const targetPosition = useRef({ x: 0, y: 0 });
//   const currentPosition = useRef({ x: 0, y: 0 });
//   const [isVisible, setIsVisible] = useState(false);

//   // Adjust these to control the offset distance from cursor
//   const offset = { x: 20, y: 20 };

//   useEffect(() => {
//     const targetElement = getTargetBlock(clientId);
//     if (!targetElement) return;

//     const handleMouseMove = (e) => {
//       if (!isVisible) return;
//       targetPosition.current = {
//         x: e.clientX + offset.x,
//         y: e.clientY + offset.y,
//       };
//     };

//     const handleEnter = () => setIsVisible(true);
//     const handleLeave = () => setIsVisible(false);

//     document.addEventListener('mousemove', handleMouseMove);
//     targetElement.addEventListener('mouseenter', handleEnter);
//     targetElement.addEventListener('mouseleave', handleLeave);

//     let animationFrame;
//     const animate = () => {
//       currentPosition.current.x += (targetPosition.current.x - currentPosition.current.x) * 0.1;
//       currentPosition.current.y += (targetPosition.current.y - currentPosition.current.y) * 0.1;

//       if (followerRef.current) {
//         followerRef.current.style.top = `${currentPosition.current.y}px`;
//         followerRef.current.style.left = `${currentPosition.current.x}px`;
//       }

//       animationFrame = requestAnimationFrame(animate);
//     };

//     animate();

//     return () => {
//       cancelAnimationFrame(animationFrame);
//       document.removeEventListener('mousemove', handleMouseMove);
//       targetElement.removeEventListener('mouseenter', handleEnter);
//       targetElement.removeEventListener('mouseleave', handleLeave);
//     };
//   }, [clientId, isVisible]);

//   return (
//     <div
//       ref={followerRef}
//       style={{
//         position: 'fixed',
//         transform: 'translate(0, 0)', 
//         pointerEvents: 'none',
//         zIndex: 9999,
//         display: isVisible ? 'flex' : 'none',
//         alignItems: 'center',
//         background: '#ff69b4',
//         color: 'white',
//         padding: '6px 10px',
//         borderRadius: '999px',
//         fontSize: '14px',
//         fontWeight: 'bold',
//         boxShadow: '0 0 8px rgba(0,0,0,0.2)',
//       }}
//     >
//       🌸 Flower
//     </div>
//   );
// };